import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtCurrency, fmtDate, fmtDay, hasAnyRole } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Download, Send, Truck, CheckCircle2, FileText, Phone, Mail, MapPin, Calendar, MessageCircle, Mail as MailIcon, PackageCheck, ShieldAlert, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { downloadPurchaseOrder } from "@/lib/pdf";

const STATUS_FLOW = {
  draft: { next: "sent", label: "Send to Supplier", icon: Send, color: "text-[#D4AF37]" },
  sent: { next: "delivered", label: "Mark Fully Delivered", icon: Truck, color: "text-emerald-400" },
  partial: { next: "delivered", label: "Mark Fully Delivered", icon: Truck, color: "text-emerald-400" },
  delivered: { next: "paid", label: "Mark Paid", icon: CheckCircle2, color: "text-blue-400" },
};

const STATUS_COLORS = {
  draft: "text-zinc-400 border-zinc-500/30 bg-zinc-500/10",
  sent: "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/10",
  partial: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  delivered: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  paid: "text-blue-400 border-blue-500/30 bg-blue-500/10",
  cancelled: "text-red-400 border-red-500/30 bg-red-500/10",
};

export default function PurchaseOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = hasAnyRole(user, ["admin", "operations"]);
  const [po, setPo] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [receiving, setReceiving] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const allPOs = await api.get("/purchase-orders");
      const found = (allPOs.data || []).find((p) => p.id === id);
      if (!found) { toast.error("Purchase order not found"); navigate("/purchasing"); return; }
      setPo(found);
      const [supRes, prRes] = await Promise.all([
        api.get("/suppliers").catch(() => ({ data: [] })),
        found.request_id ? api.get("/purchase-requests").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setSupplier((supRes.data || []).find((s) => s.id === found.supplier_id) || null);
      setRequest((prRes.data || []).find((r) => r.id === found.request_id) || null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const advance = async () => {
    const flow = STATUS_FLOW[po.status];
    if (!flow) return;
    try {
      await api.patch(`/purchase-orders/${po.id}`, { status: flow.next });
      toast.success(`PO marked as ${flow.next}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const approve = async () => {
    try {
      await api.post(`/purchase-orders/${po.id}/approve`);
      toast.success("PO approved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approval failed"); }
  };

  // Compose a short PO summary for WhatsApp / Email and open the right link
  const composeSummary = (po, supplier) => {
    const lines = [];
    lines.push(`Purchase Order ${po.po_number}`);
    lines.push(`Supplier: ${supplier?.name || "—"}`);
    if (po.expected_date) lines.push(`Expected: ${new Date(po.expected_date).toLocaleDateString("en-MY")}`);
    lines.push("");
    lines.push("Items:");
    (po.items || []).forEach((it) => {
      lines.push(`• ${it.name} — ${it.qty} ${it.unit || ""} @ RM${Number(it.unit_price || 0).toFixed(2)}`);
    });
    lines.push("");
    lines.push(`Total: RM ${Number(po.total || 0).toFixed(2)}`);
    if (po.notes) { lines.push(""); lines.push(`Notes: ${po.notes}`); }
    return lines.join("\n");
  };

  const sendWhatsApp = (po, supplier) => {
    if (!supplier?.phone) return;
    const phone = String(supplier.phone).replace(/[^0-9]/g, "");
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(composeSummary(po, supplier))}`;
    window.open(url, "_blank");
    api.patch(`/purchase-orders/${po.id}`, { sent_via: [...(po.sent_via || []), "whatsapp"] }).catch(() => {});
  };

  const sendEmail = (po, supplier) => {
    if (!supplier?.email) return;
    const subject = `Purchase Order ${po.po_number}`;
    const body = composeSummary(po, supplier) + "\n\n— HNC Event Catering Co.";
    window.location.href = `mailto:${supplier.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    api.patch(`/purchase-orders/${po.id}`, { sent_via: [...(po.sent_via || []), "email"] }).catch(() => {});
  };

  if (loading || !po) {
    return <div className="p-10 text-center text-zinc-500" data-testid="po-detail-loading">Loading purchase order…</div>;
  }

  const items = po.items || [];
  const subTotal = Number(po.total || items.reduce((s, i) => s + (Number(i.qty || 0) * Number(i.unit_price || 0)), 0));
  const flow = STATUS_FLOW[po.status];
  const NextIcon = flow?.icon;

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="po-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div className="flex items-center gap-3">
          <Button onClick={() => navigate("/purchasing")} variant="outline" data-testid="po-back" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] h-9">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Purchasing
          </Button>
          <div>
            <div className="label-eyebrow">Purchase Order</div>
            <h1 className="font-display text-3xl md:text-4xl font-mono mt-1 text-[#D4AF37]" data-testid="po-number">{po.po_number}</h1>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => downloadPurchaseOrder(po, supplier || {})} data-testid="po-pdf-download" variant="outline" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]">
            <Download className="w-4 h-4 mr-1.5" /> PDF
          </Button>
          <Button onClick={() => sendWhatsApp(po, supplier)} disabled={!supplier?.phone} data-testid="po-wa-send" variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-40">
            <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
          </Button>
          <Button onClick={() => sendEmail(po, supplier)} disabled={!supplier?.email} data-testid="po-email-send" variant="outline" className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 disabled:opacity-40">
            <MailIcon className="w-4 h-4 mr-1.5" /> Email
          </Button>
          {canManage && ["draft", "sent", "partial"].includes(po.status) && (
            <Button onClick={() => setCancelling(true)} data-testid="po-cancel-open" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
              <XCircle className="w-4 h-4 mr-1.5" /> Cancel PO
            </Button>
          )}
          {canManage && ["sent", "partial"].includes(po.status) && (
            <Button onClick={() => setReceiving(true)} data-testid="po-receive-open" variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
              <PackageCheck className="w-4 h-4 mr-1.5" /> Receive Goods
            </Button>
          )}
          {canManage && flow && (
            <Button onClick={advance} data-testid="po-advance" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B]">
              <NextIcon className="w-4 h-4 mr-1.5" /> {flow.label}
            </Button>
          )}
        </div>
      </div>

      {/* Cancelled banner */}
      {po.status === "cancelled" && (
        <div className="mb-6 p-4 rounded-sm border border-red-500/40 bg-red-500/10 text-sm text-red-400" data-testid="po-cancelled-banner">
          <div className="flex items-center gap-2 font-medium"><XCircle className="w-4 h-4" /> Cancelled by {po.cancelled_by_name || "—"}{po.cancelled_at ? ` · ${fmtDate(po.cancelled_at)}` : ""}</div>
          {po.cancelled_reason && <div className="mt-1 text-red-300/80">Reason: {po.cancelled_reason}</div>}
        </div>
      )}

      {/* Approval gate */}
      {po.approval_required && po.approval_status !== "approved" && (
        <div className="mb-6 p-4 rounded-sm border border-amber-500/40 bg-amber-500/10 flex items-center justify-between gap-3 flex-wrap" data-testid="po-approval-banner">
          <div className="flex items-center gap-2 text-amber-400 text-sm">
            <ShieldAlert className="w-4 h-4 shrink-0" />
            This PO totals {fmtCurrency(po.total)} (above RM 1,000) and needs admin approval before it can be sent, received or paid.
          </div>
          {hasAnyRole(user, ["admin"]) && (
            <Button onClick={approve} data-testid="po-approve" className="bg-amber-500 text-black hover:bg-amber-400 h-8 text-xs">
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Approve PO
            </Button>
          )}
        </div>
      )}
      {po.approval_required && po.approval_status === "approved" && (
        <div className="mb-6 px-4 py-2 rounded-sm border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-xs flex items-center gap-2" data-testid="po-approved-badge">
          <ShieldCheck className="w-3.5 h-3.5" /> Approved by {po.approved_by_name || "admin"}{po.approved_at ? ` · ${fmtDate(po.approved_at)}` : ""}
        </div>
      )}

      {/* Status + Meta strip */}
      <div className="card-lux p-6 mb-6 grid grid-cols-2 md:grid-cols-4 gap-6">
        <div>
          <div className="label-eyebrow text-[10px]">Status</div>
          <span data-testid="po-status" className={`mt-2 inline-block stage-chip capitalize text-xs ${STATUS_COLORS[po.status] || ""}`}>{po.status}</span>
        </div>
        <div>
          <div className="label-eyebrow text-[10px]">Issued</div>
          <div className="text-sm mt-2">{po.created_at ? fmtDate(po.created_at) : "—"}</div>
        </div>
        <div>
          <div className="label-eyebrow text-[10px]">Expected</div>
          <div className="text-sm mt-2 flex items-center gap-1.5">
            {po.expected_date ? <><Calendar className="w-3.5 h-3.5 text-[#D4AF37]" /> {fmtDay(po.expected_date)}</> : <span className="text-zinc-600">—</span>}
          </div>
        </div>
        <div>
          <div className="label-eyebrow text-[10px]">Total</div>
          <div className="font-display text-2xl text-[#D4AF37] mt-1" data-testid="po-total">{fmtCurrency(subTotal)}</div>
        </div>
      </div>

      {/* Supplier */}
      <div className="card-lux p-6 mb-6" data-testid="po-supplier">
        <div className="label-eyebrow mb-3">Supplier</div>
        {supplier ? (
          <div>
            <div className="font-display text-2xl">{supplier.name}</div>
            <div className="label-eyebrow text-[10px] capitalize text-zinc-500 mt-0.5">{supplier.category}</div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-zinc-300">
              {supplier.contact_person && <div className="flex items-center gap-2"><span className="text-zinc-500 text-xs">Attn:</span> {supplier.contact_person}</div>}
              {supplier.phone && <a href={`tel:${supplier.phone}`} className="flex items-center gap-2 hover:text-[#D4AF37]"><Phone className="w-3.5 h-3.5 text-zinc-500" /> {supplier.phone}</a>}
              {supplier.email && <a href={`mailto:${supplier.email}`} className="flex items-center gap-2 hover:text-[#D4AF37]"><Mail className="w-3.5 h-3.5 text-zinc-500" /> {supplier.email}</a>}
              {supplier.address && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-zinc-500" /> {supplier.address}</div>}
              {supplier.lead_time_days != null && <div className="text-xs text-zinc-500">Lead time: {supplier.lead_time_days} days</div>}
              {supplier.payment_terms && <div className="text-xs text-zinc-500">Payment terms: {supplier.payment_terms}</div>}
            </div>
          </div>
        ) : (
          <div className="text-sm text-zinc-500">Supplier not found (may have been removed).</div>
        )}
      </div>

      {/* Line Items */}
      <div className="card-lux overflow-hidden mb-6">
        <div className="p-6 pb-3">
          <div className="label-eyebrow">Line Items ({items.length})</div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-y border-[#1a1a1a] text-zinc-500">
              <th className="text-left p-4 label-eyebrow text-[10px]">Item</th>
              <th className="text-right p-4 label-eyebrow text-[10px]">Qty</th>
              <th className="text-right p-4 label-eyebrow text-[10px]">Received</th>
              <th className="text-right p-4 label-eyebrow text-[10px]">Unit Price</th>
              <th className="text-right p-4 label-eyebrow text-[10px]">Line Total</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-zinc-600 text-sm">No items on this purchase order.</td></tr>
            )}
            {items.map((it, i) => {
              const lt = Number(it.qty || 0) * Number(it.unit_price || 0);
              const rec = Number(it.qty_received || 0);
              const full = rec >= Number(it.qty || 0);
              return (
                <tr key={i} className="border-b border-[#1a1a1a]" data-testid={`po-item-${i}`}>
                  <td className="p-4">{it.name}</td>
                  <td className="p-4 text-right text-zinc-400">{it.qty} {it.unit || ""}</td>
                  <td className={`p-4 text-right ${rec > 0 ? (full ? "text-emerald-400" : "text-amber-400") : "text-zinc-600"}`} data-testid={`po-item-received-${i}`}>{rec} / {it.qty}</td>
                  <td className="p-4 text-right">{fmtCurrency(it.unit_price)}</td>
                  <td className="p-4 text-right text-zinc-100">{fmtCurrency(lt)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[#D4AF37]/30">
              <td colSpan={4} className="p-4 text-right text-zinc-500 text-xs uppercase tracking-wide">Total</td>
              <td className="p-4 text-right font-display text-xl text-[#D4AF37]">{fmtCurrency(subTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Notes + linked request */}
      {(po.notes || request) && (
        <div className="card-lux p-6 space-y-3">
          {request && (
            <div>
              <div className="label-eyebrow flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Linked Request</div>
              <div className="text-sm mt-2 text-zinc-300">{request.title} <span className="text-zinc-600 text-xs">· {request.urgency} · by {request.created_by_name}</span></div>
            </div>
          )}
          {po.notes && (
            <div>
              <div className="label-eyebrow">Notes</div>
              <div className="text-sm mt-2 text-zinc-300 whitespace-pre-line">{po.notes}</div>
            </div>
          )}
        </div>
      )}
      {receiving && (
        <ReceiveDialog po={po} onClose={() => setReceiving(false)} onDone={() => { setReceiving(false); load(); }} />
      )}
      {cancelling && (
        <CancelDialog po={po} onClose={() => setCancelling(false)} onDone={() => { setCancelling(false); load(); }} />
      )}
    </div>
  );
}

function CancelDialog({ po, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Please give a reason for cancelling"); return; }
    setSaving(true);
    try {
      await api.post(`/purchase-orders/${po.id}/cancel`, { reason: reason.trim() });
      toast.success("PO cancelled");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-md" data-testid="po-cancel-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Cancel {po.po_number}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500">The PO is kept for audit history with a red cancelled status — nothing is deleted.{(po.items || []).some((i) => i.qty_received > 0) ? " Stock already received stays in inventory." : ""}</p>
        <Textarea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason — e.g. supplier can't fulfil, price changed, duplicate order…" data-testid="po-cancel-reason" className="bg-[#050505] border-[#2a2a2a] min-h-24" />
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose} variant="outline" className="border-[#2a2a2a] rounded-sm">Keep PO</Button>
          <Button onClick={submit} disabled={saving || !reason.trim()} data-testid="po-cancel-submit" className="bg-red-600 hover:bg-red-500 text-white rounded-sm">{saving ? "Cancelling…" : "Cancel PO"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReceiveDialog({ po, onClose, onDone }) {
  const items = po.items || [];
  const [qtys, setQtys] = useState(() => items.map((it) => Math.max(0, (it.qty || 0) - (it.qty_received || 0))));
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const receipts = qtys.map((q, i) => ({ index: i, qty: Number(q) || 0 })).filter((r) => r.qty > 0);
    if (!receipts.length) { toast.error("Enter at least one quantity to receive"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/purchase-orders/${po.id}/receive`, { receipts });
      toast.success(r.data.status === "delivered" ? "All items received — PO delivered, stock updated" : "Partial receipt recorded — stock updated");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Receive failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-lg" data-testid="po-receive-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Receive Goods <span className="text-xs font-mono text-[#D4AF37] ml-2">{po.po_number}</span></DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500">Enter what actually arrived. Stock is updated immediately; anything short stays open as a partial delivery.</p>
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {items.map((it, i) => {
            const remaining = Math.max(0, (it.qty || 0) - (it.qty_received || 0));
            return (
              <div key={i} className="flex items-center gap-3 p-3 rounded-sm border border-[#1a1a1a]">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200 truncate">{it.name}</div>
                  <div className="text-[10px] text-zinc-500">Ordered {it.qty} {it.unit || ""} · received {it.qty_received || 0} · remaining {remaining}</div>
                </div>
                <Input type="number" min="0" max={remaining} value={qtys[i]} disabled={remaining <= 0}
                  onChange={(e) => setQtys((q) => q.map((v, j) => j === i ? Math.max(0, Math.min(remaining, Number(e.target.value) || 0)) : v))}
                  data-testid={`po-receive-qty-${i}`} className="w-24 h-9 bg-[#050505] border-[#2a2a2a] text-right" />
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose} variant="outline" className="border-[#2a2a2a] rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="po-receive-submit" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">{saving ? "Saving…" : "Confirm Receipt"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
